/**
 * CheckInPage
 *
 * /growth/check-ins/:id route.
 * Fetches the check-in by ID and manages a two-state machine:
 *   chat       → CheckInChat (guided conversation)
 *   confirming → CheckInSummaryConfirm (review + save)
 *
 * Transitions:
 *   chat → confirming: on auto-finalize or explicit finish-early
 *   confirming → /growth: on successful save
 */

import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loader2, AlertCircle, ArrowLeft } from "lucide-react";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { CheckInChat } from "@/components/growth/CheckInChat";
import { CheckInSummaryConfirm } from "@/components/growth/CheckInSummaryConfirm";
import {
  useCheckIn,
  useFinalizeCheckIn,
  useSaveCheckIn,
} from "@/hooks/useFocusAreaCheckIn";
import { useQuery } from "@tanstack/react-query";
import { listFocusAreas } from "@/services/focusAreas";
import type { ProgressSignal } from "@/types/focusAreaCheckIn";

type PageState = "chat" | "confirming";

export function CheckInPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [pageState, setPageState] = useState<PageState>("chat");
  const [proposedSummary, setProposedSummary] = useState("");
  const [proposedProgressSignal, setProposedProgressSignal] =
    useState<ProgressSignal>("same");

  const {
    data: checkIn,
    isLoading: checkInLoading,
    error: checkInError,
  } = useCheckIn(id);

  // Fetch all focus areas to find the one belonging to this check-in
  const { data: focusAreasData, isLoading: focusAreasLoading } = useQuery({
    queryKey: ["focusAreas", "active"],
    queryFn: () => listFocusAreas("all"),
    enabled: !!checkIn,
  });

  const focusArea = focusAreasData?.focus_areas.find(
    (fa) => fa.id === checkIn?.focus_area_id
  );

  const finalizeMutation = useFinalizeCheckIn();
  const saveMutation = useSaveCheckIn();

  const handleAutoFinalized = (summary: string, signal: ProgressSignal) => {
    setProposedSummary(summary);
    setProposedProgressSignal(signal);
    setPageState("confirming");
  };

  const handleFinishEarly = async () => {
    if (!id) return;
    try {
      const result = await finalizeMutation.mutateAsync(id);
      setProposedSummary(result.proposed_summary);
      setProposedProgressSignal(result.proposed_progress_signal);
      setPageState("confirming");
    } catch (e) {
      console.error("Failed to finalize check-in:", e);
    }
  };

  const handleSave = async (data: {
    summary: string;
    progress_signal: ProgressSignal;
  }) => {
    if (!id) return;
    await saveMutation.mutateAsync({ checkInId: id, payload: data });
    navigate("/growth");
  };

  const isLoading = checkInLoading || focusAreasLoading;

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[calc(100vh-4rem)] supports-[height:100dvh]:h-[calc(100dvh-4rem)]">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </Layout>
    );
  }

  if (checkInError || !checkIn) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center h-[calc(100vh-4rem)] supports-[height:100dvh]:h-[calc(100dvh-4rem)] gap-4">
          <AlertCircle className="h-8 w-8 text-destructive" />
          <p className="text-muted-foreground">
            {checkInError?.message ?? "Check-in not found"}
          </p>
          <Button variant="outline" onClick={() => navigate("/growth")}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Growth
          </Button>
        </div>
      </Layout>
    );
  }

  if (!focusArea) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center h-[calc(100vh-4rem)] supports-[height:100dvh]:h-[calc(100dvh-4rem)] gap-4">
          <AlertCircle className="h-8 w-8 text-destructive" />
          <p className="text-muted-foreground">Focus area not found</p>
          <Button variant="outline" onClick={() => navigate("/growth")}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Growth
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="flex flex-col h-[calc(100vh-4rem)] supports-[height:100dvh]:h-[calc(100dvh-4rem)]">
        {/* Top nav */}
        <div className="px-4 py-2 border-b bg-background shrink-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/growth")}
            className="gap-1 -ml-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Growth
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          {pageState === "chat" ? (
            <CheckInChat
              focusArea={focusArea}
              checkIn={checkIn}
              onAutoFinalized={handleAutoFinalized}
              onFinishEarly={() => void handleFinishEarly()}
            />
          ) : (
            <CheckInSummaryConfirm
              proposedSummary={proposedSummary}
              proposedProgressSignal={proposedProgressSignal}
              onSave={(data) => void handleSave(data)}
              isSaving={saveMutation.isPending}
            />
          )}
        </div>
      </div>
    </Layout>
  );
}
